import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 3000; // 3 seconds - more aggressive

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);
  
  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }
  
  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetInboxAllCaches = () => {
  debounceMap.clear();
};

// Async thunk for fetching active team members
export const fetchActiveMembers = createAsyncThunk(
  "inboxAll/fetchActiveMembers",
  async (_, { rejectWithValue }) => {
    // Debounce check
    if (shouldDebounce('fetchActiveMembers')) {
      console.log('🚫 Debounced fetchActiveMembers call');
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/inbox/getactivemembers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const inboxAllSlice = createSlice({
  name: "inboxAll",
  initialState: {
    selectedContactID: null,
    /** After tab switch / delete / sidebar clear, block MessageCard URL→selectContact briefly so stale /chat/:id cannot re-open the thread */
    urlContactSyncSuppressUntil: 0,
    activeMembers: [],
    // True once fetchActiveMembers has actually resolved at least once (even to an
    // empty list) — distinct from `!activeMembers.length`, which is also true while
    // a fetch is still pending/was dropped and can't tell "genuinely nobody online"
    // apart from "never fetched yet". Consumers (InboxSettings.jsx) use this to know
    // whether a rejected/debounced attempt still needs a retry.
    activeMembersLoaded: false,
    loading: false,
    error: null,
  },
  reducers: {
    setselectedContactID(state, action) {
      const selected = action.payload;
      if (selected == null && state.selectedContactID != null) console.warn("[DL-TRACE] setselectedContactID(null)", new Error().stack);
      state.selectedContactID = selected;
      if (selected != null) {
        state.urlContactSyncSuppressUntil = 0;
      } else {
        // Same as clearSelectedContactID: stale /chat/:id must not immediately re-select via MessageCard
        state.urlContactSyncSuppressUntil = Date.now() + 1500;
      }
    },
    addActiveMember(state, action) {
      // Check if the member already exists to avoid duplicates
      const memberExists = state.activeMembers.find(
        (member) => member._id === action.payload._id
      );
      if (!memberExists) {
        state.activeMembers.push(action.payload);
      }
    },
    removeActiveMember(state, action) {
      // Filter out the member with the matching ID
      state.activeMembers = state.activeMembers.filter(
        (member) => member._id !== action.payload
      );
    },
    clearSelectedContactID(state) {
      console.log("Clearing selected contact ID");
      state.selectedContactID = null;
      state.urlContactSyncSuppressUntil = Date.now() + 1500;
    },
    clearUrlContactSyncSuppress(state) {
      state.urlContactSyncSuppressUntil = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.activeMembers = action.payload;
        state.activeMembersLoaded = true;
      })
      .addCase(fetchActiveMembers.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      });
  },
});

export default inboxAllSlice.reducer;
// Export actions
export const {
  setselectedContactID,
  addActiveMember,
  removeActiveMember,
  clearSelectedContactID,
  clearUrlContactSyncSuppress,
} = inboxAllSlice.actions;
